import { useEffect } from 'react';
import toast from "react-hot-toast";

const SEOHead = () => {
    useEffect(() => {
        // Update document title
        document.title = 'Infinity by Platypus - India\'s First Intelligent Pet Care Ecosystem';

        // Meta tags
        const metaTags = [
            { name: 'description', content: 'Introducing Infinity - The intelligent collar powering India\'s first total pet wellness ecosystem. Smart health monitoring, walker booking, and behavior insights for your beloved pets.' },
            { name: 'keywords', content: 'pet care, smart collar, dog walker, pet health, pet monitoring, India, Platypus, Infinity' },
            { name: 'author', content: 'Platypus' },
            { name: 'viewport', content: 'width=device-width, initial-scale=1.0' },

            // Open Graph tags
            { property: 'og:title', content: 'Infinity by Platypus - Intelligent Pet Care Ecosystem' },
            { property: 'og:description', content: 'India\'s first intelligent collar and app ecosystem for complete pet wellness, safety, and care.' },
            { property: 'og:type', content: 'website' },
            { property: 'og:url', content: 'https://landing.theplatypus.in/' },
            { property: 'og:image', content: 'https://landing.theplatypus.in/og-image.jpg' },

            // Twitter Card tags
            { name: 'twitter:card', content: 'summary_large_image' },
            { name: 'twitter:title', content: 'Infinity by Platypus - Intelligent Pet Care' },
            { name: 'twitter:description', content: 'Revolutionary pet care ecosystem with smart collar and comprehensive app.' },
            { name: 'twitter:image', content: 'https://landing.theplatypus.in/twitter-image.jpg' },

            // Additional SEO tags
            { name: 'robots', content: 'index, follow' },
            { name: 'language', content: 'English' },
            { name: 'theme-color', content: '#FFE135' },
        ];

        // Create or update meta tags
        metaTags.forEach(({ name, property, content }) => {
            const selector = name ? `meta[name="${name}"]` : `meta[property="${property}"]`;
            let meta = document.querySelector(selector);

            if (!meta) {
                meta = document.createElement('meta');
                if (name) meta.name = name;
                if (property) meta.setAttribute('property', property);
                document.head.appendChild(meta);
            }

            meta.content = content;
        });

        // Structured data for rich snippets
        const structuredData = {
            "@context": "https://schema.org",
            "@type": "Product",
            "name": "Infinity Smart Pet Collar",
            "brand": {
                "@type": "Brand",
                "name": "Platypus"
            },
            "description": "India's first intelligent pet collar with comprehensive wellness ecosystem",
            "category": "Pet Care Technology",
            "offers": {
                "@type": "Offer",
                "availability": "https://schema.org/PreOrder",
                "url": "https://landing.theplatypus.in/"
            },
            "manufacturer": {
                "@type": "Organization",
                "name": "Third Planet Solutions Private Limited",
                "url": "https://theplatypus.in"
            }
        };

        // Add structured data script
        let scriptTag = document.querySelector('script[type="application/ld+json"]');
        if (!scriptTag) {
            scriptTag = document.createElement('script');
            scriptTag.type = 'application/ld+json';
            document.head.appendChild(scriptTag);
        }
        scriptTag.textContent = JSON.stringify(structuredData);

        // Preload critical resources
        const preloadLinks = [
            { href: '/Home8.png', as: 'image' },
            { href: '/Vector.png', as: 'image' },
            { href: '/doggroup.png', as: 'image' },
        ];

        preloadLinks.forEach(({ href, as }) => {
            const link = document.createElement('link');
            link.rel = 'preload';
            link.href = href;
            link.as = as;
            document.head.appendChild(link);
        });

    }, []);

    return null;
};

// Custom hook for form error handling
export const useFormErrorHandling = () => {
    const handleFormError = (error, fieldName) => {
        const errorMessages = {
            required: `${fieldName} is required`,
            email: 'Please enter a valid email address',
            phone: 'Please enter a valid phone number',
            minLength: `${fieldName} is too short`,
            maxLength: `${fieldName} is too long`,
            network: 'Network error. Please check your connection.',
            server: 'Server error. Please try again later.',
            duplicate: 'This information has already been submitted.',
        };

        const errorType = error.type || 'server';
        const message = errorMessages[errorType] || error.message || 'An error occurred';

        toast.error(message, {
            duration: 4000,
            position: 'top-center',
        });
    };

    const validateField = (name, value, rules = {}) => {
        const errors = [];

        if (rules.required && !value.trim()) {
            errors.push({ type: 'required', field: name });
        }

        if (rules.email && value && !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(value)) {
            errors.push({ type: 'email', field: name });
        }

        if (rules.phone && value && !/^[\+]?[1-9][\d]{0,15}$/.test(value.replace(/\s/g, ''))) {
            errors.push({ type: 'phone', field: name });
        }

        if (rules.minLength && value.length < rules.minLength) {
            errors.push({ type: 'minLength', field: name });
        }

        return errors;
    };

    return { handleFormError, validateField };
};

export default SEOHead;
